<template>
  <div sign-in>
    <Layout
      :app="app"
      header="Sign in to continue"
      title="Welcome back!"
    >
      <ul
        v-if="errors"
        class="error"
      >
        <li
          v-for="err in errors"
          :key="err.message"
        >
          {{ err.message }}
        </li>
      </ul>

      <template v-if="!mfaTokenRequired">
        <Input
          v-model="email"
          :autofocus="true"
          class="input"
          label="Email address"
          type="email"
          placeholder="me@company.com"
          @enter="submit"
        />
        <Input
          v-model="password"
          class="input"
          label="Password"
          type="password"
          placeholder=""
          @enter="submit"
        />
      </template>

      <template v-if="mfaRequired">
        You need to set up two factor authentication in order to continue.
        <Button
          class="button"
          label="Continue"
        />
      </template>

      <template v-if="mfaTokenRequired">
        <CodeInput
          v-model="mfaToken"
          :autofocus="true"
          label="Input a token from your authentication app"
          size="6"
          @enter="submit"
        />
      </template>

      <div
        v-if="loading"
        class="loading"
      >
        <Spinner />
      </div>
      <Button
        v-else
        class="button"
        label="Sign in"
        @click="submit"
      />

      <div
        v-if="app && !isAdminApp"
        class="links"
      >
        <a
          class="link"
          :href="app && app.url"
        >Back to {{ app.name }}</a>
        <router-link
          v-if="!mfaTokenRequired"
          class="link"
          :to="`/${appKey}/forgot-password`"
        >
          Forgot your password?
        </router-link>
      </div>
    </Layout>
  </div>
</template>

<script>
  import gql from 'graphql-tag';
  import { ADMIN_APP_KEY } from '@/globals';
  import { setToken } from '@/lib/token';
  import { resolveReturnUrl } from '@/lib/redirect';
  import Layout from './layout.vue';
  import Button from '../../components/button.vue';
  import CodeInput from '../../components/code-input.vue';
  import Input from '../../components/input.vue';
  import Spinner from '../../components/spinner.vue';
  import app from '../../mixins/app';

  const getError = (errors, error) => errors.find((e) => e.message === error);

  export default {
    name: 'SignIn',
    components: {
      Button,
      CodeInput,
      Input,
      Layout,
      Spinner,
    },
    mixins: [app],
    data() {
      return {
        email: '',
        password: '',
        errors: null,
        loading: false,
        mfaRequired: false,
        mfaToken: '',
        mfaTokenRequired: false,
      };
    },
    computed: {
      isAdminApp() {
        return this.appKey === ADMIN_APP_KEY;
      },
    },
    methods: {
      getReturnUrl(token) {
        const returnParam = this.$route.query.return || '/';
        return resolveReturnUrl(returnParam, this.app.redirect, token);
      },
      redirectUrl(token) {
        if (this.$route.params.returnRoute) {
          this.$router.push({ name: this.$route.params.returnRoute, params: { token } });
        } else {
          window.location = this.getReturnUrl(token);
        }
      },
      submit() {
        this.errors = null;
        this.loading = true;
        this.$apollo.mutate({
          mutation: gql`mutation ($email: String!, $password: String!, $mfaToken: String, $key: String!) {
          login (email: $email, password: $password, mfaToken: $mfaToken, key: $key) {
            token
            enforceMfa
          }
        }`,
          variables: {
            email: this.email,
            password: this.password,
            mfaToken: this.mfaToken,
            key: this.appKey,
          },
        }).then(({ data: { login } }) => {
          this.loading = false;
          if (login.token) {
            setToken(login.token, this.isAdminApp);
            if (login.enforceMfa) {
              this.$router.push({
                name: '2fa',
                params: {
                  token: login.token,
                  return: this.getReturnUrl(login.token),
                },
              });
            } else {
              this.redirectUrl(login.token);
            }
          }
        }).catch((error) => {
          this.loading = false;
          const mfaTokenRequired = getError(error.graphQLErrors, 'Mfa token required.');
          if (mfaTokenRequired) {
            this.mfaTokenRequired = true;
            return;
          }
          const invalidMfaToken = getError(error.graphQLErrors, 'Invalid token.');
          if (invalidMfaToken) {
            this.password = null;
            this.mfaToken = null;
            this.mfaTokenRequired = false;
          }
          this.errors = error.graphQLErrors;
        });
      },
    },
  };
</script>

<style scoped lang="stylus">
  @require '~@/style/colors'
  @require '~@/style/type'

  .input
    margin-top: 30px
    &:first-of-type
      margin-top: 0

  .button, .loading
    width: 100%
    margin-top: 60px

  .loading
    height: 50px
    display: flex
    align-items: center
    justify-content: center

  .links
    @extend $type-small
    display: block
    width: 100%
    text-align: center
    margin-top: 30px
    a
      display: inline-block
      margin-left: 10px
      padding-right: 10px
      border-right: 1px solid #aaa
      &:last-of-type
        border-right: none

  .error
    @extend $type-small, $type-medium
    color: #a00
    background-color: rgba(205,92,92,.1)
    text-align: center
    list-style: none
    margin: 0 0 20px 0
    padding: 10px

</style>
